import { Address } from 'cpfr-common';
import { InfoIcon, NotificationStatus } from '@cp-shared-19/frontend-ui';
import { DataOverview, Button, Notification } from '@vwfs-bronson/bronson-react';
import { isEmpty } from 'lodash';
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { emptyObjectOrAllValuesAreUndefined } from 'utils';
import { NoConnectionNotification } from '../../../notifications/no-connection/NoConnectionNotification';
import { NotFoundError } from '../../not-found-error/NotFoundError';
import { AddressChangeNotification } from '../address-change-notification/AddressChangeNotification';
import { EditStatus } from '../enums';
import { AddressComponent } from './address/AddressComponent';
import { ModificationStatus } from 'components/my-profile/modification-status';

export type ConsultViewProps = {
    address?: Address;
    billingAddress?: Address;
    isPrivateCustomer: boolean;
    startEditing: (addressType: string) => void;
    editPostalAddressStatus: EditStatus;
    editBillingAddressStatus: EditStatus;
    isAddressChangePending: boolean;
    isPostalAddressChangePending: boolean;
    isBillingAddressChangePending: boolean;
    isContactInformationChangePending: boolean;
    isEligibleNpaiNotification: boolean;
};

export const ConsultView: React.FC<ConsultViewProps> = ({
    address,
    billingAddress,
    isPrivateCustomer,
    startEditing,
    editPostalAddressStatus,
    editBillingAddressStatus,
    isAddressChangePending,
    isPostalAddressChangePending,
    isBillingAddressChangePending,
    isContactInformationChangePending,
    isEligibleNpaiNotification,
}) => {
    const { t } = useTranslation('my-profile');
    const translationPrefix = 'address.consult-view';

    const [pendingPostal, setPendingPostal] = useState(false);
    const [pendingBilling, setPendingBilling] = useState(false);

    useEffect(() => {
        if (editPostalAddressStatus !== 'SUCCESS' && editPostalAddressStatus !== 'NOT_PERFORMED') {
            setPendingPostal(false);
        }
    }, [editPostalAddressStatus]);

    useEffect(() => {
        if (editBillingAddressStatus !== 'SUCCESS' && editBillingAddressStatus !== 'NOT_PERFORMED') {
            setPendingBilling(false);
        }
    }, [editBillingAddressStatus]);

    const displayNotification = () => {
        return (
            <Notification
                status={NotificationStatus.warning}
                testId={'npai-warning'}
                className={'u-mb'}
                title={t(`${translationPrefix}.notification`)}
                visible
                showCloseButton={false}
            />
        );
    };

    const getUpdatingCondition = (isBillingAddressUpdate?: boolean) => {
        if (!isPrivateCustomer) {
            if (isBillingAddressUpdate) {
                return isBillingAddressChangePending;
            } else {
                return isPostalAddressChangePending;
            }
        } else {
            return isAddressChangePending;
        }
    };

    const pendingPostalCondition = isPrivateCustomer ? pendingPostal : false;

    const triggerPostalEditing = useCallback(
        (event?: { preventDefault?: () => void }) => {
            event?.preventDefault?.();
            setPendingPostal(true);
            startEditing('postal');
        },
        [startEditing],
    );

    const handlePostalEditKeyDown = useCallback(
        (event: React.KeyboardEvent) => {
            if (event.key === 'Enter' || event.key === ' ') {
                triggerPostalEditing(event);
            }
        },
        [triggerPostalEditing],
    );

    const triggerBillingEditing = useCallback(
        (event?: { preventDefault?: () => void }) => {
            event?.preventDefault?.();
            startEditing('billing');
            setPendingBilling(true);
        },
        [startEditing],
    );

    const handleBillingEditKeyDown = useCallback(
        (event: React.KeyboardEvent) => {
            if (event.key === 'Enter' || event.key === ' ') {
                triggerBillingEditing(event);
            }
        },
        [triggerBillingEditing],
    );

    return (
        <>
            <DataOverview
                titleLevel={2}
                title={t(`${translationPrefix}.title`)}
                buttonLabel={t('contact-details.consult-view.edit-button')}
                buttonProps={{
                    tabIndex: 0,
                    onClick: triggerPostalEditing,
                    onKeyDown: handlePostalEditKeyDown,
                    testId: 'editPostalAddressButton',
                    disabled:
                        getUpdatingCondition() ||
                        isContactInformationChangePending ||
                        editPostalAddressStatus === 'SUCCESS',
                }}
            >
                {!address ? (
                    <NotFoundError />
                ) : isEmpty(address) ? (
                    <NoConnectionNotification />
                ) : (
                    <>
                        <AddressChangeNotification lastEditStatus={editPostalAddressStatus} />
                        {getUpdatingCondition() || editPostalAddressStatus === 'SUCCESS' ? (
                            <>
                                <ModificationStatus />
                            </>
                        ) : (
                            <>
                                {isEligibleNpaiNotification && displayNotification()}
                                <AddressComponent address={address} />
                            </>
                        )}
                    </>
                )}
            </DataOverview>
            {!isPrivateCustomer && billingAddress && emptyObjectOrAllValuesAreUndefined(billingAddress as object) ? (
                <article className="c-data-overview">
                    <AddressChangeNotification lastEditStatus={editBillingAddressStatus} />
                    {!(getUpdatingCondition(true) || editBillingAddressStatus === 'SUCCESS') && (
                        <>
                            <Button
                                icon="semantic-forward"
                                iconReversed
                                small
                                link={true}
                                testId="addBillingAddressButton"
                                disabled={
                                    getUpdatingCondition(true) ||
                                    isContactInformationChangePending ||
                                    pendingPostalCondition
                                }
                                onClick={() => {
                                    startEditing('billing');
                                    setPendingBilling(true);
                                }}
                            >
                                {t('address.consult-view.add-billing-label')}
                            </Button>
                            <InfoIcon text={t('address.consult-view.add-billing-tooltip')} />
                        </>
                    )}
                </article>
            ) : (
                !isPrivateCustomer && (
                    <DataOverview
                        titleLevel={2}
                        title={t(`${translationPrefix}.title-sme`)}
                        buttonLabel={t('contact-details.consult-view.edit-button')}
                        buttonProps={{
                            tabIndex: 0,
                            onClick: triggerBillingEditing,
                            onKeyDown: handleBillingEditKeyDown,
                            testId: 'editBillingAddressButton',
                            disabled:
                                getUpdatingCondition(true) ||
                                isContactInformationChangePending ||
                                pendingBilling ||
                                editBillingAddressStatus === 'SUCCESS',
                        }}
                    >
                        <AddressChangeNotification lastEditStatus={editBillingAddressStatus} />
                        {getUpdatingCondition(true) || editBillingAddressStatus === 'SUCCESS' ? (
                            <>
                                <ModificationStatus />
                            </>
                        ) : (
                            <>
                                {isEligibleNpaiNotification && displayNotification()}
                                <AddressComponent address={billingAddress} />
                            </>
                        )}
                    </DataOverview>
                )
            )}
        </>
    );
};
